import { useEffect, useState } from "react";
import { Icon } from "../lib/Icon";
import { Avatar, EmptyState, StickerCard, Stars } from "../lib/components";
import { useCatalog } from "../contexts/CatalogContext";
import { fetchMatches, type MatchEntry, type MatchUser } from "../api/matches";
import { useToast } from "../lib/toast";
import { ProposeTradeModal } from "./ProposeTradeModal";
import type { Route } from "../App";

type Variant = "list" | "swipe" | "grid";

interface Props {
  go: (r: Route) => void;
  openChat: (userId: number) => void;
  variant?: Variant;
}

export function MatchesScreen({ go, openChat, variant = "list" }: Props) {
  const [tab, setTab] = useState<"perfect" | "partial">("perfect");
  const [matches, setMatches] = useState<MatchEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [proposalSent, setProposalSent] = useState<Record<number, boolean>>({});
  const [proposeFor, setProposeFor] = useState<MatchEntry | null>(null);
  const toast = useToast();

  useEffect(() => {
    setLoading(true);
    fetchMatches(50)
      .then(setMatches)
      .catch((e) => toast(`Error cargando matches: ${e}`))
      .finally(() => setLoading(false));
  }, [toast]);

  const perfect = matches.filter((m) => m.i_give_count > 0 && m.you_give_count > 0);
  const partial = matches.filter((m) => m.i_give_count === 0 || m.you_give_count === 0);
  const list = tab === "perfect" ? perfect : partial;

  const propose = (entry: MatchEntry) => {
    setProposeFor(entry);
  };

  const onProposalSent = (userId: number) => {
    setProposalSent((p) => ({ ...p, [userId]: true }));
    setProposeFor(null);
    toast("Propuesta enviada · revísala en 'Tratos'");
  };

  if (loading) {
    return <div style={{ padding: 40, textAlign: "center", color: "var(--fg-muted)" }}>Buscando matches…</div>;
  }

  return (
    <div className="page">
      <div style={{ display: "flex", alignItems: "flex-end", marginBottom: 16 }}>
        <div>
          <h1 style={{ fontFamily: "var(--font-display)", fontSize: 32, fontWeight: 800, margin: 0, letterSpacing: "-0.02em" }}>Matches automáticos</h1>
          <p style={{ color: "var(--fg-muted)", margin: "4px 0 0" }}>Coleccionistas que tienen lo que tú necesitas y necesitan lo que tú tienes.</p>
        </div>
        <div style={{ flex: 1 }} />
      </div>

      <div className="tabs">
        <button className={"tab " + (tab === "perfect" ? "active" : "")} onClick={() => setTab("perfect")}>
          <Icon name="sparkle" size={14} /> Match perfecto <span className="count">{perfect.length}</span>
        </button>
        <button className={"tab " + (tab === "partial" ? "active" : "")} onClick={() => setTab("partial")}>
          Sugerencias <span className="count">{partial.length}</span>
        </button>
      </div>

      {list.length === 0 ? (
        <EmptyState
          icon="sparkle"
          title={matches.length === 0 ? "Aún no hay otros coleccionistas con tu álbum" : "Sin matches en esta pestaña"}
          body={matches.length === 0
            ? "Comparte PaniniNilabs con tus amigos para que se registren y carguen sus láminas. Apenas haya coincidencias aparecerán aquí."
            : "Mira la otra pestaña o agrega más láminas a tu álbum."}
        />
      ) : variant === "list" ? (
        <div className="col" style={{ gap: 16 }}>
          {list.map((m) => <MatchRow key={m.user.id} entry={m} sent={proposalSent[m.user.id]} onPropose={() => propose(m)} onChat={() => openChat(m.user.id)} />)}
        </div>
      ) : variant === "grid" ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 16 }}>
          {list.map((m) => <MatchTile key={m.user.id} entry={m} sent={proposalSent[m.user.id]} onPropose={() => propose(m)} onChat={() => openChat(m.user.id)} />)}
        </div>
      ) : (
        <div className="swipe-stack-wrap">
          {list.slice(0, 1).map((m) => <SwipeCard key={m.user.id} entry={m} sent={proposalSent[m.user.id]} onPropose={() => propose(m)} onChat={() => openChat(m.user.id)} />)}
          <div style={{ textAlign: "center", color: "var(--fg-muted)", fontSize: 13 }}>
            Quedan <b>{Math.max(0, list.length - 1)}</b> matches por revisar
          </div>
        </div>
      )}

      {proposeFor && (
        <ProposeTradeModal
          toUserId={proposeFor.user.id}
          toUserName={proposeFor.user.name}
          /* Pre-llenamos con TODAS las matches del algoritmo. El modal
             internamente equilibra give vs receive y deja al usuario ajustar. */
          prefilledGive={proposeFor.i_give_sticker_ids}
          prefilledReceive={proposeFor.you_give_sticker_ids}
          onClose={() => setProposeFor(null)}
          onSent={() => onProposalSent(proposeFor.user.id)}
        />
      )}
    </div>
  );
}

interface RowProps {
  entry: MatchEntry;
  sent?: boolean;
  onPropose: () => void;
  onChat: () => void;
}

function MatchRow({ entry, sent, onPropose, onChat }: RowProps) {
  const { stickerById, teamById } = useCatalog();
  const { user, score, i_give_count, you_give_count, i_give_sticker_ids, you_give_sticker_ids } = entry;
  return (
    <div className="match-row">
      <div className="match-side">
        <UserHead user={user} />
        <div style={{ marginTop: 12 }}>
          <div className="tiny bold muted" style={{ marginBottom: 6 }}>{user.name.split(" ")[0]} TE PUEDE DAR ({you_give_count})</div>
          <div className="match-stickers">
            {you_give_sticker_ids.slice(0, 8).map((id) => renderSticker(id, stickerById, teamById))}
            {you_give_count > 8 && <div className="chip" style={{ alignSelf: "center" }}>+{you_give_count - 8}</div>}
          </div>
        </div>
      </div>
      <div className="match-arrow">
        <div className="match-score">{score}</div>
        <div className="tiny bold">MATCH</div>
        <div className="chip primary" style={{ marginTop: 4 }}>
          <Icon name="sparkle" size={10} /> {score >= 5 ? "Perfecto" : "Bueno"}
        </div>
      </div>
      <div className="match-side" style={{ alignItems: "flex-end" }}>
        <div className="tiny bold muted" style={{ marginBottom: 6 }}>TÚ LE DAS ({i_give_count})</div>
        <div className="match-stickers" style={{ justifyContent: "flex-end" }}>
          {i_give_sticker_ids.slice(0, 8).map((id) => renderSticker(id, stickerById, teamById))}
          {i_give_count > 8 && <div className="chip" style={{ alignSelf: "center" }}>+{i_give_count - 8}</div>}
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
          <button className="btn btn-secondary btn-sm" onClick={onChat}>
            <Icon name="chat" size={14} /> Chat
          </button>
          <button className="btn btn-primary btn-sm" onClick={onPropose} disabled={sent}>
            {sent ? <><Icon name="check" size={14} /> Enviada</> : <><Icon name="swap" size={14} /> Proponer</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function SwipeCard({ entry, sent, onPropose, onChat }: RowProps) {
  const { stickerById, teamById } = useCatalog();
  const { user, score, i_give_sticker_ids, you_give_sticker_ids } = entry;
  return (
    <div className="swipe-card">
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
        <Avatar user={user} size="lg" />
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 17 }}>{user.name}</div>
          <div className="tiny muted">{user.handle ?? "—"} · {user.city ?? "—"}</div>
        </div>
        <div className="match-score" style={{ width: 56, height: 56, fontSize: 26 }}>{score}</div>
      </div>
      <div style={{ background: "var(--primary-soft)", padding: 12, borderRadius: 10 }}>
        <div className="tiny bold" style={{ color: "var(--primary)", marginBottom: 8 }}>TE DARÁ</div>
        <div className="match-stickers">
          {you_give_sticker_ids.slice(0, 6).map((id) => renderSticker(id, stickerById, teamById))}
        </div>
      </div>
      <div style={{ height: 12 }} />
      <div style={{ background: "var(--bg-subtle)", padding: 12, borderRadius: 10 }}>
        <div className="tiny bold muted" style={{ marginBottom: 8 }}>LE DARÁS</div>
        <div className="match-stickers">
          {i_give_sticker_ids.slice(0, 6).map((id) => renderSticker(id, stickerById, teamById))}
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginTop: 16 }}>
        <button className="btn btn-secondary" style={{ justifyContent: "center" }}><Icon name="x" size={18} /></button>
        <button className="btn btn-secondary" onClick={onChat} style={{ justifyContent: "center" }}><Icon name="chat" size={18} /></button>
        <button className="btn btn-primary" onClick={onPropose} style={{ justifyContent: "center" }} disabled={sent}>
          {sent ? <Icon name="check" size={18} /> : <Icon name="heart" size={18} />}
        </button>
      </div>
    </div>
  );
}

function MatchTile({ entry, sent, onPropose, onChat }: RowProps) {
  const { stickerById, teamById } = useCatalog();
  const { user, score, i_give_count, you_give_count, i_give_sticker_ids, you_give_sticker_ids } = entry;
  return (
    <div className="card" style={{ padding: 0, overflow: "hidden" }}>
      <div style={{ background: `linear-gradient(135deg, ${user.avatar_color}, color-mix(in oklab, ${user.avatar_color} 60%, black))`, padding: 16, color: "white", display: "flex", alignItems: "center", gap: 12 }}>
        <Avatar user={user} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 15 }}>{user.name}</div>
          <div style={{ fontSize: 12, opacity: 0.85 }}>{user.city ?? "—"} · {user.trades_completed} 🔄</div>
        </div>
        <div style={{ background: "rgba(255,255,255,0.2)", padding: "4px 10px", borderRadius: 999, fontWeight: 800, fontFamily: "var(--font-display)" }}>×{score}</div>
      </div>
      <div style={{ padding: 14 }}>
        <div className="tiny bold muted">TE DARÁ ({you_give_count})</div>
        <div className="match-stickers" style={{ marginTop: 6 }}>
          {you_give_sticker_ids.slice(0, 5).map((id) => renderSticker(id, stickerById, teamById))}
        </div>
        <div className="tiny bold muted" style={{ marginTop: 12 }}>LE DARÁS ({i_give_count})</div>
        <div className="match-stickers" style={{ marginTop: 6 }}>
          {i_give_sticker_ids.slice(0, 5).map((id) => renderSticker(id, stickerById, teamById))}
        </div>
        <div style={{ display: "flex", gap: 6, marginTop: 14 }}>
          <button className="btn btn-secondary btn-sm" onClick={onChat} style={{ flex: 1, justifyContent: "center" }}><Icon name="chat" size={14} /></button>
          <button className="btn btn-primary btn-sm" onClick={onPropose} style={{ flex: 2, justifyContent: "center" }} disabled={sent}>
            {sent ? "Enviada ✓" : "Proponer trato"}
          </button>
        </div>
      </div>
    </div>
  );
}

function UserHead({ user }: { user: MatchUser }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      <Avatar user={user} size="lg" />
      <div>
        <div style={{ fontWeight: 700, fontSize: 16 }}>{user.name}</div>
        <div className="tiny muted">{user.handle ?? "—"} · {user.city ?? "—"}</div>
        <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
          <Stars value={user.stars_avg} size={11} />
          <span className="tiny muted">{Number(user.stars_avg || 0).toFixed(1)} · {user.trades_completed} intercambios</span>
        </div>
      </div>
    </div>
  );
}

function renderSticker(stickerId: number, stickerById: ReturnType<typeof useCatalog>["stickerById"], teamById: ReturnType<typeof useCatalog>["teamById"]) {
  const s = stickerById(stickerId);
  if (!s) return null;
  const t = teamById(s.team_id);
  return <StickerCard key={s.id} sticker={s} teamColor={t?.color} teamCode={t?.code} count={2} showDup={false} />;
}
